import type { ComboPricingRule } from '../policy/business-policy.ts';
import { resolveComboMatches, type ComboMatch, type PricingUnit } from './combo.ts';

export interface ConfirmedComboMatchInput {
  readonly ruleId: string;
  readonly consumedUnits: readonly { readonly lineId: string; readonly unitIndex: number }[];
}

const unitKey = (unit: Pick<PricingUnit, 'lineId' | 'unitIndex'>): string => `${unit.lineId}#${unit.unitIndex}`;

export function resolveConfirmedComboMatches(
  units: readonly PricingUnit[],
  rules: readonly ComboPricingRule[],
  confirmed: readonly ConfirmedComboMatchInput[],
): readonly ComboMatch[] {
  const unitsByKey = new Map(units.map((unit) => [unitKey(unit), unit]));
  const globallyConsumed = new Set<string>();
  const matches: ComboMatch[] = [];

  for (const input of confirmed) {
    const rule = rules.find((candidate) => candidate.enabled && candidate.ruleId === input.ruleId);
    if (!rule) throw new Error(`PRICING_EXPLICIT_COMBO_RULE_NOT_FOUND:${input.ruleId}`);
    if (input.consumedUnits.length === 0) throw new Error(`PRICING_EXPLICIT_COMBO_INVALID:${input.ruleId}`);

    const selected: PricingUnit[] = [];
    for (const ref of input.consumedUnits) {
      if (!Number.isSafeInteger(ref.unitIndex) || ref.unitIndex < 0 || !ref.lineId?.trim()) {
        throw new Error(`PRICING_EXPLICIT_COMBO_UNIT_INVALID:${input.ruleId}`);
      }
      const key = unitKey(ref);
      if (globallyConsumed.has(key)) throw new Error(`PRICING_EXPLICIT_COMBO_UNIT_DUPLICATE:${key}`);
      const unit = unitsByKey.get(key);
      if (!unit) throw new Error(`PRICING_EXPLICIT_COMBO_UNIT_NOT_FOUND:${key}`);
      selected.push(unit);
    }

    const resolved = resolveComboMatches(selected, [rule]);
    if (resolved.length !== 1 || resolved[0].consumedUnits.length !== selected.length) {
      throw new Error(`PRICING_EXPLICIT_COMBO_INVALID:${input.ruleId}`);
    }
    const resolvedKeys = new Set(resolved[0].consumedUnits.map(unitKey));
    if (selected.some((unit) => !resolvedKeys.has(unitKey(unit)))) {
      throw new Error(`PRICING_EXPLICIT_COMBO_INVALID:${input.ruleId}`);
    }

    for (const unit of selected) globallyConsumed.add(unitKey(unit));
    matches.push({
      ...resolved[0],
      consumedUnits: input.consumedUnits.map((unit) => ({ ...unit })),
    });
  }

  return matches;
}
